"use client";

import type { FC, MouseEvent } from "react";
import { mergeClasses } from "@huub-web/utils";

import { Flex } from "../../Flex";
import { Icon } from "../../Icon";
import type { IconName } from "../../../theme";
import { Text } from "../../Text";
import { Tooltip, type TooltipProps } from "../../Tooltip";

import {
  buttonStyle,
  buttonContentStyle,
  gradientBorderStyle,
  contentWrapperStyle,
} from "./ContentMenuItem.css";
import { focusVisibleOffset } from "../../../theme/css/focusVisible.css";

export type ContentMenuItemProps = {
  active?: boolean;
  index?: number;
  disabled?: boolean;
  icon?: IconName;
  tooltip?: string;
  label?: string;
  variant?: "default" | "navigation" | "ellipsis";
  tooltipPlacement?: TooltipProps["placement"];
  onClick: (e: MouseEvent) => void;
};

/**
 * ContentMenuItem component renders a single menu item
 * for a content menu section with icon, index, or label display,
 * or a navigation button (prev/next) with icon and tooltip
 */
export const ContentMenuItem: FC<ContentMenuItemProps> = ({
  active,
  index,
  disabled,
  icon,
  tooltip,
  tooltipPlacement = "bottom",
  label,
  variant = "default",
  onClick,
}) => (
  <li>
    <Tooltip
      value={disabled ? undefined : tooltip}
      placement={tooltipPlacement}
    >
      <Flex
        as="button"
        align="center"
        justify="center"
        onClick={onClick}
        disabled={disabled}
        className={mergeClasses(
          buttonStyle({ variant, active, disabled }),
          focusVisibleOffset,
        )}
      >
        <div className={gradientBorderStyle({ active })} />
        <div className={contentWrapperStyle({ active, variant })} />

        {icon && index === undefined && (
          <Icon
            icon={icon}
            size="s"
            className={buttonContentStyle({ active })}
            color={active ? "gradientA" : "currentColor50"}
          />
        )}

        {((index !== undefined && !icon) || variant === "ellipsis") && (
          <Text
            align="center"
            variant="bodyL"
            className={buttonContentStyle({ active })}
          >
            {variant === "ellipsis" ? "..." : index}
          </Text>
        )}

        {!icon && index === undefined && variant !== "ellipsis" && (
          <Text ellipsis className={buttonContentStyle({ active })}>
            {label}
          </Text>
        )}
      </Flex>
    </Tooltip>
  </li>
);
